import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  DEFAULT_ROWS_PER_PAGE,
  ExpandableTable,
  LazyTableRow,
  TableCellType,
} from "@knowfully/ui.common";
import useGlobalContext from "hooks/globalData";
import { useNavigate } from "react-router-dom";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { OrgExamAttemptSummary } from "services/organizationPortalClient";

const getStyles = (theme: Theme): { [key: string]: SxProps } => ({
  dialogTitle: {
    color: theme.palette.primary.main,
    fontSize: "1rem",
    fontWeight: 600,
  },
  closeButton: {
    position: "absolute",
    width: 24,
    height: 24,
    right: 12,
    top: 12,
  },
  emptyState: {
    textAlign: "center",
    color: theme.palette.text.secondary,
    py: 2,
  },
  errorState: {
    textAlign: "center",
    color: theme.palette.error.main,
    py: 2,
  },
});

export type ExamResultsDialogState = {
  completionId: string;
  courseTitle?: string;
};

type ExamResultsDialogProps = {
  open: boolean;
  onClose: () => void;
  completionId?: string;
  courseTitle?: string;
};

type AttemptActionsCellProps = {
  value: OrgExamAttemptSummary;
  onView: (attemptId: string) => void;
};

function AttemptActionsCell({
  value,
  onView,
}: Readonly<AttemptActionsCellProps>) {
  return (
    <IconButton onClick={() => onView(value.id)}>
      <VisibilityIcon />
    </IconButton>
  );
}

const getCells = (
  t: ReturnType<typeof useIntl>["formatMessage"],
  onView: (attemptId: string) => void
): Array<TableCellType> => [
  {
    accessor: "endDateTime",
    type: "date",
    label: t({ id: "examDate" }),
    noSort: true,
  },
  {
    accessor: "formattedScore",
    type: "text",
    label: t({ id: "score" }),
    align: "center",
    noSort: true,
  },
  {
    accessor: "passed",
    type: "boolean",
    label: t({ id: "result" }),
    align: "center",
    TrueComponent: <CheckCircleOutlineIcon color="success" />,
    noSort: true,
  },
  {
    accessor: "attemptNumber",
    type: "text",
    label: t({ id: "attemptNumber" }),
    align: "center",
    noSort: true,
  },
  {
    accessor: "",
    type: "node",
    label: "",
    align: "right",
    noSort: true,
    View: ({ value }: { value: OrgExamAttemptSummary }) => (
      <AttemptActionsCell value={value} onView={onView} />
    ),
  },
];

export default function ExamResultsDialog({
  open,
  onClose,
  completionId,
  courseTitle,
}: Readonly<ExamResultsDialogProps>) {
  const styles = getStyles(useTheme());
  const { certificatesService, orgExamResultUrl } = useGlobalContext();
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();

  const { data, isFetching, isError } = useQuery(
    ["org-exam-results", completionId],
    () => certificatesService.getExamResults(completionId!),
    { enabled: open && !!completionId }
  );

  const cells = getCells(t, (attemptId) => {
    if (completionId) {
      navigate(orgExamResultUrl(completionId, attemptId));
    }
  });

  const attempts: LazyTableRow[] = data?.data ?? [];

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>
        <Box sx={styles.dialogTitle}>
          {courseTitle ?? t({ id: "examResults" })}
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={styles.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ExpandableTable
          {...{
            "aria-label": t({ id: "examResults" }),
            rowCount: attempts.length,
            rows: attempts,
            loading: isFetching,
            dense: true,
            cells,
            page: 0,
            setPage: () => {},
            order: "desc" as const,
            setOrder: () => {},
            orderBy: "endDateTime",
            setOrderBy: () => {},
            rowsPerPage: DEFAULT_ROWS_PER_PAGE,
            setRowsPerPage: () => {},
            allowPagination: false,
            collapsable: false,
          }}
        />
        {isError && (
          <Box sx={styles.errorState}>
            <T id="examResultsFetchError" />
          </Box>
        )}
        {!isFetching && !isError && attempts.length === 0 && (
          <Box sx={styles.emptyState}>
            <T id="noExamAttemptsFound" />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
